/**
 * Capabilities the UI renders by. The list of names lives here so a typo fails the build, but WHO holds each
 * capability is decided by the API: `backend/Common.Utilities/Authorization/capabilities.json`, served on the
 * token-exchange response (`Capabilities`) and by `GET api/Account/Users/Me/Permissions`. The frontend never
 * grants anything; every endpoint enforces its own roles. Add a name here and in that JSON together.
 */

export const ORGANIZATION_ROLES = ['Administrator', 'Recruiter', 'HiringManager', 'HiringTeam'] as const;

export type OrganizationRole = (typeof ORGANIZATION_ROLES)[number];

/** Numeric values as the API serialises them on member and invitation models. */
export const ROLE_VALUES: Record<OrganizationRole, number> = {
  Administrator: 1,
  Recruiter: 2,
  HiringManager: 3,
  HiringTeam: 4,
};

export const ROLE_LABELS: Record<OrganizationRole, string> = {
  Administrator: 'Organisation Administrator',
  Recruiter: 'Recruiter',
  HiringManager: 'Hiring Manager',
  HiringTeam: 'Hiring Team',
};

export const ROLE_DESCRIPTIONS: Record<OrganizationRole, string> = {
  Administrator: 'Everything: jobs, candidates, members, billing, integrations, settings and deletion.',
  Recruiter:
    'Runs recruiting end to end: jobs, candidates, interviews, emails and templates. No members, billing, integrations or organisation settings.',
  HiringManager:
    'Reads jobs and candidates, including comments, emails and scorecards. Writes only comments and their own scorecard.',
  HiringTeam:
    'Reads the candidate overview and profile and submits a scorecard for a human round. No comments, emails or reports.',
};

/** Accepts the API's name, its number, or a number-as-string; anything else is not a role. */
export function parseRole(value: unknown): OrganizationRole | null {
  if (typeof value === 'string') {
    if ((ORGANIZATION_ROLES as readonly string[]).includes(value)) return value as OrganizationRole;
    const asNumber = Number(value);
    if (!Number.isNaN(asNumber)) return parseRole(asNumber);
    return null;
  }
  if (typeof value === 'number') {
    const match = (Object.keys(ROLE_VALUES) as OrganizationRole[]).find((r) => ROLE_VALUES[r] === value);
    return match ?? null;
  }
  return null;
}

/**
 * Every capability name the UI asks about. Must match the keys of the API's capabilities.json; an unknown name
 * is simply never granted, so a mismatch hides a control rather than exposing one.
 */
export const CAPABILITIES = [
  // Jobs
  'jobs.view',
  'jobs.create',
  'jobs.edit',
  'jobs.changeStatus',
  'jobs.duplicate',
  'jobs.delete',
  'jobs.workflow.view',
  'jobs.workflow.edit',
  'jobs.evaluation.view',
  'jobs.evaluation.edit',
  'jobs.forms.view',
  'jobs.forms.edit',
  'jobs.interviewHub.view',
  'jobs.interviewHub.act',
  'jobs.compare',
  'jobs.report',
  'jobs.follow',
  'jobs.settings',

  // Candidates
  'candidates.viewBoard',
  'candidates.add',
  'candidates.moveStage',
  'candidates.reject',
  'candidates.bulkActions',
  'candidates.sendInterview',
  'candidates.delete',
  'candidates.viewOverview',
  'candidates.viewProfile',
  'candidates.viewScorecards',
  'candidates.viewRisk',
  'candidates.reviewIntegrity',
  'candidates.viewEvaluation',
  'candidates.viewFormAnswers',
  'candidates.submitScorecard',
  'candidates.comments.read',
  'candidates.comments.write',
  'candidates.comments.manageOthers',
  'candidates.emails.read',
  'candidates.emails.send',
  'candidates.downloadReport',
  'candidates.reprocess',

  // Knowledge base
  'knowledgeBase.ask',
  'knowledgeBase.manage',

  // Organisation settings
  'settings.info',
  'settings.jobBoardEmbed',
  'settings.hiringTeam',
  'settings.agentBuilder.create',
  'settings.agentBuilder.edit',
  'settings.agentBuilder.delete',
  'settings.templates',
  'settings.members.view',
  'settings.members.manage',
  'settings.invitations',
  'settings.billing',
  'settings.integrations',
  'settings.knowledgeBase',
] as const;

export type RoleAction = (typeof CAPABILITIES)[number];

/** `(action) => boolean`, the shape every consumer receives. */
export type CanFn = (action: RoleAction) => boolean;

/** Builds a `can` from the capability names the API returned. Nothing is granted until they arrive. */
export function canFromCapabilities(capabilities: readonly string[] | null | undefined): CanFn {
  const set = new Set(capabilities ?? []);
  return (action: RoleAction) => set.has(action);
}

/** The Organisation Settings tabs, each with the capability it needs, in the order the sidebar shows them. */
export const SETTINGS_TAB_ACTIONS = {
  info: 'settings.info',
  jobBoardEmbed: 'settings.jobBoardEmbed',
  integrations: 'settings.integrations',
  billing: 'settings.billing',
  members: 'settings.members.view',
  invitations: 'settings.invitations',
  knowledgeBase: 'settings.knowledgeBase',
  formTemplates: 'settings.templates',
  emailTemplates: 'settings.templates',
} as const satisfies Record<string, RoleAction>;

/** Where a member lands when opening /organization-settings with no tab: the first tab they may see. */
export function defaultSettingsTab(can: CanFn): keyof typeof SETTINGS_TAB_ACTIONS | null {
  const order = Object.keys(SETTINGS_TAB_ACTIONS) as (keyof typeof SETTINGS_TAB_ACTIONS)[];
  return order.find((tab) => can(SETTINGS_TAB_ACTIONS[tab])) ?? null;
}
